import React, { useContext, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Typography,
  Paper,
  useTheme,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DrawerHeader } from '@components/drawer-header';
import { GlobalContext } from 'context/userContext';
import {
  MapOverlayConfig,
  OperationConfig,
  USER_MACHINE_ACCESS_MODES,
  getUserMachineAccessHelpText,
  normalizeOperationConfig,
  resolveImageUrl,
} from '@utils/operation-config-options';
import MapIcon from '@mui/icons-material/Map';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import { listOperationConfigs, saveOperationConfig } from './api';
import { MapOverlayConfigDialog } from './components/map-overlay-config-dialog';

const PROTECTED_FIELDS = new Set(['_id']);
const SELECT_FIELDS = new Set(['USER_MACHINE_ACCESS_MODE', 'ACCESSED_BY', 'DOCUMENT_CONFIGURATION', 'USE_MAINTENANCE_MODULE']);
const HIDDEN_FIELDS = new Set(['_id', 'DATABASE_PASSWORD', 'DATABASE_PATH', 'DATABASE_INDEX', 'DATABASE_USER', 'MAP_OVERLAY']);

function stringifyValue(value: any): string {
  if (Array.isArray(value)) return value.join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value, null, 2);
  if (value === undefined || value === null) return '';
  return String(value);
}

function parseValue(original: any, value: string): any {
  if (Array.isArray(original)) {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }
  if (typeof original === 'number') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? original : parsed;
  }
  if (typeof original === 'boolean') {
    return value === 'true';
  }
  return value;
}

function OperationConfigCard({ operation, allOperations }: { operation: OperationConfig; allOperations: string[] }) {
  const queryClient = useQueryClient();
  const normalized = useMemo(() => normalizeOperationConfig(operation), [operation]);
  const [draft, setDraft] = useState<OperationConfig>(normalized);
  const [localError, setLocalError] = useState<string | null>(null);
  const [overlayDialogOpen, setOverlayDialogOpen] = useState<boolean>(false);

  React.useEffect(() => {
    setDraft(normalized);
  }, [normalized]);

  const saveMutation = useMutation({
    mutationFn: saveOperationConfig,
    onSuccess: () => {
      setLocalError(null);
      queryClient.invalidateQueries({ queryKey: ['operation-configs'] });
    },
    onError: (err: any) => {
      setLocalError(err?.response?.data?.message || err?.message || 'Erro ao salvar configuração.');
    },
  });

  const keys = Object.keys(draft)
    .filter((key) => !HIDDEN_FIELDS.has(key))
    .sort((a, b) => {
      if (a === 'DATABASE_NAME') return -1;
      if (b === 'DATABASE_NAME') return 1;
      if (SELECT_FIELDS.has(a) && !SELECT_FIELDS.has(b)) return -1;
      if (!SELECT_FIELDS.has(a) && SELECT_FIELDS.has(b)) return 1;
      return a.localeCompare(b);
    });

  const userMachineHelp = getUserMachineAccessHelpText(draft.USER_MACHINE_ACCESS_MODE);

  return (
    <Card elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ p: 2.5, background: 'linear-gradient(135deg, #7b102a 0%, #a51033 55%, #111827 100%)', color: 'white' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flexWrap="wrap">
          <Box>
            <Typography variant="h6" fontFamily="Saira" fontWeight={700}>{draft.DATABASE_NAME}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>Global.operacoes — parâmetros de operação e regras de uso</Typography>
          </Box>
          <Chip label="Automni admin" color="default" sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white' }} />
        </Stack>
      </Box>
      <CardContent>
        <Stack spacing={2.5}>
          {localError && <Alert severity="error">{localError}</Alert>}

          <Box>
            <Grid container spacing={2}>


              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={Array.from(new Set([...allOperations, ...(Array.isArray(draft.ACCESSED_BY) ? draft.ACCESSED_BY : [])]))}
                  value={Array.isArray(draft.ACCESSED_BY) ? draft.ACCESSED_BY : []}
                  onChange={(_, newValue) => setDraft({ ...draft, ACCESSED_BY: newValue })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Operações com acesso (ACCESSED_BY)"
                      helperText="Selecione quais operações podem acessar os dados desta base."
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <CardContent sx={{ pb: '16px !important' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={draft.USE_MAINTENANCE_MODULE}
                          onChange={(e) => setDraft({ ...draft, USE_MAINTENANCE_MODULE: e.target.checked })}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>Ativar Módulo de Manutenção</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Exibe relatórios e gráficos de manutenção (preventivas e corretivas) no dashboard e menu lateral.
                          </Typography>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: draft.MAP_OVERLAY?.enabled && draft.MAP_OVERLAY?.imageUrl ? '#93c5fd' : '#e2e8f0',
                    bgcolor: draft.MAP_OVERLAY?.enabled && draft.MAP_OVERLAY?.imageUrl ? '#f0f9ff' : 'rgba(0,0,0,0.02)',
                  }}
                >
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <MapIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight={700}>
                              Overlay de Imagem no Mapa (Planta / Layout Operacional)
                            </Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Permite carregar uma imagem (planta arquitetônica, vista aérea ou layout da fábrica/aeródromo)
                            e calibrar a rotação, escala e posição diretamente sobre o mapa da operação.
                          </Typography>
                        </Box>

                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<MapIcon />}
                          onClick={() => setOverlayDialogOpen(true)}
                          sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 2 }}
                        >
                          {draft.MAP_OVERLAY?.imageUrl ? 'Editar Overlay no Mapa' : 'Configurar Overlay no Mapa'}
                        </Button>
                      </Stack>

                      {draft.MAP_OVERLAY?.imageUrl ? (
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                          <Grid container spacing={2} alignItems="center">
                            {/* Preview thumbnail */}
                            <Grid item xs={12} sm={3} md={2}>
                              <Box
                                component="img"
                                src={resolveImageUrl(draft.MAP_OVERLAY.imageUrl)}
                                alt="Overlay preview"
                                sx={{
                                  width: '100%',
                                  height: 90,
                                  objectFit: 'cover',
                                  borderRadius: 1.5,
                                  border: '1px solid #cbd5e1',
                                }}
                              />
                            </Grid>

                            {/* Details & Status chips */}
                            <Grid item xs={12} sm={9} md={10}>
                              <Stack spacing={1}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={0.5}>
                                  <Chip
                                    size="small"
                                    icon={<CheckCircleIcon />}
                                    label={draft.MAP_OVERLAY.enabled ? 'Overlay Ativo' : 'Overlay Desativado'}
                                    color={draft.MAP_OVERLAY.enabled ? 'success' : 'default'}
                                  />
                                  <Chip
                                    size="small"
                                    icon={<LockIcon />}
                                    label={draft.MAP_OVERLAY.locked ? 'Posição Travada' : 'Posição Destravada'}
                                    color={draft.MAP_OVERLAY.locked ? 'info' : 'warning'}
                                  />
                                  <Chip
                                    size="small"
                                    label={`Transparência: ${Math.round((draft.MAP_OVERLAY.opacity ?? 0.75) * 100)}%`}
                                    variant="outlined"
                                  />
                                  {draft.MAP_OVERLAY.rotation !== undefined && (
                                    <Chip
                                      size="small"
                                      label={`Rotação: ${draft.MAP_OVERLAY.rotation}°`}
                                      variant="outlined"
                                    />
                                  )}
                                </Stack>

                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                                  <strong>Arquivo:</strong> {draft.MAP_OVERLAY.imageUrl}
                                </Typography>

                                <Stack direction="row" spacing={2} alignItems="center">
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={draft.MAP_OVERLAY.enabled ?? true}
                                        onChange={(e) =>
                                          setDraft({
                                            ...draft,
                                            MAP_OVERLAY: {
                                              ...draft.MAP_OVERLAY!,
                                              enabled: e.target.checked,
                                            },
                                          })
                                        }
                                        color="primary"
                                      />
                                    }
                                    label={<Typography variant="body2">Exibir no mapa dos dashboards</Typography>}
                                  />

                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() => setDraft({ ...draft, MAP_OVERLAY: null })}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Remover configuração
                                  </Button>
                                </Stack>
                              </Stack>
                            </Grid>
                          </Grid>
                        </Paper>
                      ) : (
                        <Alert severity="info" sx={{ py: 0.5, bgcolor: 'rgba(2, 136, 209, 0.08)' }}>
                          Nenhum overlay de imagem configurado para esta operação. Clique no botão acima para carregar uma imagem e posicioná-la no mapa.
                        </Alert>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Colunas de Global.operacoes</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Todas as colunas conhecidas são exibidas para diagnóstico. Senhas reais nunca são retornadas pela API; o valor “unchanged” preserva a senha atual.
            </Typography>
            <Grid container spacing={2}>
              {keys.map((key) => {
                const value = draft[key];
                const disabled = PROTECTED_FIELDS.has(key) || key === 'DATABASE_NAME' || SELECT_FIELDS.has(key);

                if (key === 'DOCUMENT_EXPIRATION_CHECK') {
                  // Don't render legacy DOCUMENT_EXPIRATION_CHECK
                  return null;
                }

                return (
                  <Grid item xs={12} md={6} lg={4} key={key}>
                    <TextField
                      fullWidth
                      label={key}
                      value={stringifyValue(value)}
                      disabled={disabled}
                      multiline={typeof value === 'object'}
                      minRows={typeof value === 'object' ? 3 : undefined}
                      helperText={disabled ? 'Campo protegido ou controlado acima' : Array.isArray(value) ? 'Separe itens por vírgula' : 'Editável por Automni admin'}
                      onChange={(event: any) => setDraft({ ...draft, [key]: parseValue(value, event.target.value) })}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="outlined" onClick={() => setDraft(normalized)} disabled={saveMutation.isPending}>Descartar alterações</Button>
            <Button
              variant="contained"
              onClick={() => saveMutation.mutate(draft)}
              disabled={saveMutation.isPending}
              startIcon={saveMutation.isPending ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar configuração'}
            </Button>
          </Stack>
        </Stack>
      </CardContent>

      <MapOverlayConfigDialog
        open={overlayDialogOpen}
        operationName={draft.DATABASE_NAME}
        initialConfig={draft.MAP_OVERLAY}
        onClose={() => setOverlayDialogOpen(false)}
        onSave={(newOverlay) => setDraft({ ...draft, MAP_OVERLAY: newOverlay })}
      />
    </Card>
  );
}

export function OperationsConfigPage() {
  const { mainUser, operation: selectedOperation } = useContext(GlobalContext);
  const isAutomniAdmin = mainUser?.roles?.some?.((role: string) => ['ADMINISTRADOR', 'SUPERADMINISTRADOR'].includes(role)) && mainUser?.employed_by?.trim?.().toLowerCase() === 'automni';
  const theme = useTheme();

  const configsQuery = useQuery({
    queryKey: ['operation-configs'],
    queryFn: listOperationConfigs,
    enabled: isAutomniAdmin,
  });

  return (
    <DrawerHeader title="Configurações das Operações">
      <Stack spacing={3} sx={{ maxWidth: 1320, mx: 'auto' }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2.5, md: 3 },
            bgcolor: 'background.paper',
            borderLeft: `6px solid ${theme.palette.primary.main}`,
            borderRadius: 2,
          }}
        >
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 600 }}>
              ADMINISTRAÇÃO DE BANCO DE DADOS
            </Typography>
            <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Configuração Global de Tenant</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Viewscreen restrito a administradores Automni para ajustar regras operacionais críticas, acesso de usuários e parametrização de checklist sem expor credenciais dos bancos de dados isolados.
            </Typography>
          </Box>
        </Paper>

        {!isAutomniAdmin ? (
          <Alert severity="warning">
            Página visível apenas para administradores da Automni. Se você precisa operar esta configuração, entre com uma conta Automni admin.
          </Alert>
        ) : configsQuery.isLoading ? (
          <Stack alignItems="center" mt={4}>
            <CircularProgress />
            <Typography variant="body2" mt={2}>Carregando operações...</Typography>
          </Stack>
        ) : configsQuery.isError ? (
          <Alert severity="error">Erro ao carregar configurações das operações.</Alert>
        ) : (
          <Stack spacing={3}>
            {(configsQuery.data || [])
              .filter((operation) => operation.DATABASE_NAME === selectedOperation)
              .map((operation) => (
                <OperationConfigCard
                  key={operation.DATABASE_NAME}
                  operation={operation}
                  allOperations={(configsQuery.data || []).map(op => op.DATABASE_NAME)}
                />
              ))}
            {(configsQuery.data || []).filter((operation) => operation.DATABASE_NAME === selectedOperation).length === 0 && (
              <Alert severity="info">Nenhuma operação selecionada ou encontrada em Global.operacoes.</Alert>
            )}
          </Stack>
        )}
      </Stack>
    </DrawerHeader>
  );
}
